import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../api';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, BarChart, Bar } from 'recharts';
import type { Stats, ManagerStats, EstablishmentStats } from '../types';

export default function StatsPage() {
  const { user } = useAuth();
  const [period, setPeriod] = useState(7);
  const [stats, setStats] = useState<Stats | ManagerStats | EstablishmentStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadStats();
  }, [user, period]);

  const loadStats = async () => {
    if (!user) return;

    try {
      setLoading(true);
      setError(null);

      if (user.role === 'employee') {
        const data = await api.getEmployeeStats(user.id, period);
        setStats(data);
      } else if (user.role === 'manager') {
        const data = await api.getManagerStats(user.id, period);
        setStats(data);
      } else if (user.role === 'admin') {
        // Admin sees stats for first manager (or all employees)
        // Get all users and find a manager to get aggregated stats
        const users = await api.getUsers();
        const managers = users.filter(u => u.role === 'manager');
        if (managers.length > 0) {
          const data = await api.getManagerStats(managers[0].id, period);
          setStats(data);
        } else {
          // No managers, show employee stats for all employees
          const employees = users.filter(u => u.role === 'employee');
          if (employees.length > 0) {
            // Aggregate stats from all employees
            let total = 0, completed = 0, overdue = 0;
            for (const emp of employees) {
              try {
                const empStats = await api.getEmployeeStats(emp.id, period);
                total += empStats.total;
                completed += Math.round(empStats.total * empStats.completed_pct / 100);
                overdue += empStats.overdue_count;
              } catch {}
            }
            setStats({
              total,
              completed_pct: total > 0 ? Math.round(completed / total * 100) : 0,
              overdue_count: overdue,
              time_series: [],
              avg_time_to_complete: null
            } as Stats);
          }
        }
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load stats');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="loading">
        <div className="spinner"></div>
      </div>
    );
  }

  if (error) {
    return <div className="error-message">{error}</div>;
  }

  if (!stats) {
    return <div>Нет данных для отображения</div>;
  }

  const timeSeriesData = stats.time_series || [];

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>Статистика</h2>
        <select
          className="form-select"
          style={{ width: 'auto' }}
          value={period}
          onChange={(e) => setPeriod(parseInt(e.target.value))}
        >
          <option value="7">За 7 дней</option>
          <option value="14">За 14 дней</option>
          <option value="30">За 30 дней</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-grid">
        <div className="stat-card">
          <div className="stat-value">{stats.total}</div>
          <div className="stat-label">Всего задач</div>
        </div>
        <div className="stat-card">
          <div className="stat-value">{stats.completed_pct}%</div>
          <div className="stat-label">Выполнено</div>
        </div>
        <div className="stat-card">
          <div className="stat-value" style={{ color: stats.overdue_count > 0 ? 'var(--danger)' : undefined }}>
            {stats.overdue_count}
          </div>
          <div className="stat-label">Просрочено</div>
        </div>
      </div>

      {/* Time Series Chart */}
      {timeSeriesData.length > 0 && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>Динамика задач</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={timeSeriesData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="date" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Line type="monotone" dataKey="created" stroke="#4F46E5" name="Создано" />
                <Line type="monotone" dataKey="completed" stroke="#10B981" name="Выполнено" />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* Manager Stats - Employee Breakdown */}
      {user?.role === 'manager' && 'employee_stats' in stats && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>По сотрудникам</h3>
          <div className="chart-container">
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.employee_stats}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="user_name" tick={{ fontSize: 12 }} />
                <YAxis allowDecimals={false} />
                <Tooltip />
                <Bar dataKey="total" fill="#4F46E5" name="Всего" />
                <Bar dataKey="completed" fill="#10B981" name="Выполнено" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          <div className="user-list" style={{ marginTop: '1rem' }}>
            {stats.employee_stats.map((emp) => (
              <div key={emp.user_id} className="user-item">
                <span className="user-name">{emp.user_name}</span>
                <span>{emp.completed}/{emp.total} ({emp.completed_pct}%)</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Establishment Stats - By Status */}
      {user?.role === 'admin' && 'task_by_status' in stats && (
        <div className="card">
          <h3 className="card-title" style={{ marginBottom: '1rem' }}>По статусам</h3>
          <div className="user-list">
            {stats.task_by_status.map((item) => (
              <div key={item.status} className="user-item">
                <span className="user-name">{getStatusText(item.status)}</span>
                <span>{item.count}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function getStatusText(status: string): string {
  const map: Record<string, string> = {
    pending: 'Ожидает',
    in_progress: 'В работе',
    questions: 'Вопросы',
    completed: 'Выполнено',
    cancelled: 'Отменено',
  };
  return map[status] || status;
}
